import axios from 'axios'

export default class API {
	static getExternal = (url, type, headers) => {
		return new Promise(async (resolve, reject) => {
			try {
				let opts = {
					method: "get",
					url: url
				};

				if (type == 'file') {
					opts.responseType = 'blob'
				};

				if (headers) {
					opts.headers = headers;
				}

				const response = await axios(opts);

				resolve(response);
			} catch (err) {
				console.error(JSON.stringify(err));

				reject(err);
			}
		})
	}

	// Only consumer is Tracking.vue, whose route is disabled per INFS-1191 —
	// this block (and the VUE_APP_TRACK_API_URL/USERNAME/PASSWORD env vars it
	// reads) is currently dead code, kept alongside Tracking.vue for possible
	// future use.
	static TRACK_TOKEN_KEY = 'airaav2_token';
	// Re-login this many ms before the server-stated expiry to avoid edge-of-window failures.
	static TRACK_TOKEN_RENEW_BUFFER_MS = 60 * 60 * 1000;

	static async _loginTrackApi() {
		const url = `${process.env.VUE_APP_TRACK_API_URL}/auth/login`;
		const response = await axios.post(url, {
			identifier: process.env.VUE_APP_TRACK_API_USERNAME,
			password: process.env.VUE_APP_TRACK_API_PASSWORD,
		});

		const data = response.data || {};
		const token = data.access_token || data.token;
		if (!token) throw new Error('AIRAA v2 login returned no token');

		// Prefer server-stated expiry; fall back to documented 7-day TTL.
		const ttlSeconds = data.expires_in || 7 * 24 * 60 * 60;
		const expiresAt = Date.now() + ttlSeconds * 1000;

		localStorage.setItem(this.TRACK_TOKEN_KEY, JSON.stringify({ token, expiresAt }));
		return token;
	}

	static async trackToken() {
		try {
			const raw = localStorage.getItem(this.TRACK_TOKEN_KEY);
			if (raw) {
				const { token, expiresAt } = JSON.parse(raw);
				if (token && expiresAt && Date.now() < expiresAt - this.TRACK_TOKEN_RENEW_BUFFER_MS) {
					return token;
				}
			}
		} catch (e) {
			// fall through to re-login on any parse/storage error
		}
		return this._loginTrackApi();
	}

	static getTrack = (url, type) => {
		return new Promise(async (resolve, reject) => {
			const send = async (token) => {
				const opts = {
					method: 'get',
					url: url,
					headers: { Authorization: `Bearer ${token}` },
				};
				if (type == 'file') opts.responseType = 'blob';
				return axios(opts);
			};

			try {
				const token = await this.trackToken();
				try {
					resolve(await send(token));
				} catch (err) {
					// Token rejected (revoked, server restart, clock skew): clear and retry once.
					if (err.response && err.response.status === 401) {
						localStorage.removeItem(this.TRACK_TOKEN_KEY);
						const fresh = await this._loginTrackApi();
						resolve(await send(fresh));
						return;
					}
					throw err;
				}
			} catch (err) {
				console.error(JSON.stringify(err?.response?.data || err?.message || err));
				reject(err);
			}
		});
	}

	static getFromCms = (moduleName, headers) => {
		return new Promise(async (resolve, reject) => {
			try {
				let opts = {
					method: "get",
					url: `${this.cmsApiUrl()}/${moduleName}`,
				};

				if (headers) {
					opts.headers = headers;
				}

				const response = await axios(opts);

				resolve(response);
			} catch (err) {
				console.error(JSON.stringify(err));

				reject(err);
			}
		})
	}

	static getFromIraa = (moduleName, headers) => {
		return new Promise(async (resolve, reject) => {
			try {
				let opts = {
					method: "get",
					url: `${this.iraaApiUrl()}/${moduleName}`,
					headers: { 'Authorization': `Bearer ${this.iraaToken()}` }
				};

				if (headers) {
					opts.headers = headers;
				}

				const response = await axios(opts);

				resolve(response);
			} catch (err) {
				console.error(JSON.stringify(err));

				reject(err);
			}
		})
	}

	static get(moduleName, type, options) {
		return new Promise(async (resolve, reject) => {
			try {
				const opts = this._opts(moduleName, 'get');
				opts.timeout = 30000;
				if (type == 'file') {
					opts.responseType = 'blob'
				};
				if (options && options.signal) {
					opts.signal = options.signal;
				}
				const response = await axios(opts);
				resolve(response);
			} catch (err) {
				if (axios.isCancel(err)) {
					reject(err);
					return;
				}
				console.error(JSON.stringify(err.response));
				if (err.response && err.response.status == 401) {
					localStorage.clear();
					window.location = '/';
				}
				reject(err);
			}
		})
	}

	static post(moduleName, data) {
		return new Promise(async (resolve, reject) => {
			try {
				const opts = this._opts(moduleName, 'post', data);
				const response = await axios(opts);
				resolve(response);
			} catch (err) {
				console.error(JSON.stringify(err.response));
				if (err.response && err.response.status == 401) {
					localStorage.clear();
					window.location = '/';
				}
				reject(err);
			}
		})
	}

	static forgetPassword(moduleName, data) {
		return new Promise(async (resolve, reject) => {
			try {
				let opts = {
					method: 'post',
					url: `${this.apiUrl()}/${moduleName}`
				}

				if (data) {
					opts.data = data
				}
				const response = await axios(opts);
				resolve(response);
			} catch (err) {
				console.error(JSON.stringify(err.response));
				if (err.response && err.response.status == 401) {
					localStorage.clear();
					window.location = '/';
				}
				reject(err);
			}
		})
	}

	static postForm(moduleName, data, timeout) {
		return new Promise(async (resolve, reject) => {
			try {
				const opts = this._opts(moduleName, 'post', data);
				opts.headers['Content-Type'] = 'multipart/form-data';

				if (timeout) {
					opts.timeout = timeout;
				}

				const response = await axios(opts);
				resolve(response)
			} catch (err) {
				if (err.response && err.response.status == 401) {
					localStorage.clear();
					window.location = '/';
				}
				reject(err);
			}
		})
	}

	static put(moduleName, data) {
		return new Promise(async (resolve, reject) => {
			try {
				const opts = this._opts(moduleName, 'put', data);
				const response = await axios(opts);
				resolve(response)
			} catch (err) {
				if (err.response && err.response.status == 401) {
					localStorage.clear();
					window.location = '/';
				}
				reject(err);
			}
		})
	}

	static delete(moduleName, data) {
		return new Promise(async (resolve, reject) => {
			try {
				const opts = this._opts(moduleName, 'delete', data);
				const response = await axios(opts);
				resolve(response)
			} catch (err) {
				if (err.response && err.response.status == 401) {
					localStorage.clear();
					window.location = '/';
				}
				reject(err);
			}
		})
	}

	static _opts(moduleName, method, data) {
		let token = localStorage.getItem('token');
		const opts = {
			method: method,
			url: `${this.apiUrl()}/${moduleName}`
		};

		if (token) {
			opts.headers = {
				'Authorization': `Bearer ${token}`
			};
		}

		if (data) {
			opts.data = data
		};

		return opts;
	};

	static apiUrl() {
		return process.env.VUE_APP_API_URL;
	};

	static iraaApiUrl() {
		return process.env.VUE_APP_IRAA_API_URL;
	};

	static iraaToken() {
		return process.env.VUE_APP_IRAA_TOKEN;
	};

	static cmsApiUrl() {
		return process.env.VUE_APP_CMS_API_URL;
	};

	static cmsUrl() {
		return process.env.VUE_APP_CMS_URL;
	};

	static cmsNodUrl() {
		return process.env.VUE_APP_CMS_NOD_URL;
	};

	static droneApiUrl() {
		return process.env.VUE_APP_DRONE_API_URL;
	};
};